import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Banknote, Download, Lock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Dialog } from '../../components/nursery/accounts/AccountBits';
import { CashCount } from '../../components/nursery/counter/CashCount';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { money, round2 } from '../../utils/nurseryMoney';
import { breakdownTotal, cleanBreakdown, denominationLabel, parseAmount, varianceText } from '../../utils/counterCash';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import type { CashBreakdown, TillMovement, TillMovementKind, TillSession } from '../../types/nurseryCounter';

/**
 * One till: its float, every cash movement with who made it, what it should
 * hold, and — once closed — what was counted and the difference. Cash in and
 * cash out need a reason. Closing counts the drawer; a till that is out needs
 * a note. Nothing here can be edited or deleted: a mistake is put right with
 * another movement.
 */

const KIND_LABEL: Record<TillMovementKind, string> = {
  sale: 'Sale',
  refund: 'Refund',
  pay_in: 'Cash in',
  pay_out: 'Cash out',
};
/** Which way a movement moves the till. */
const SIGN: Record<TillMovementKind, 1 | -1> = { sale: 1, refund: -1, pay_in: 1, pay_out: -1 };

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function TillDetail() {
  const { id } = useParams<{ id: string }>();
  const timezone = useOrgTimezone();
  const { hasAccess: canWork } = useModuleLicense('nursery', ['manager', 'admin']);
  const [session, setSession] = useState<TillSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'pay_in' | 'pay_out' | 'close' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getTill(Number(id));
      if (res.success) setSession(res.data);
      else setError(errorMessage(res, 'The till could not be loaded'));
    } catch (e) {
      setError(errorMessage(e, 'The till could not be loaded'));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error
          ? <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
          : <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />}
      </div>
    );
  }

  const t = session.totals;
  const open = session.status === 'open';
  const movements = session.movements ?? [];

  const exportCsv = () => {
    const rows = [
      ['Time', 'What', 'Order', 'Amount', 'Handed over', 'Change', 'Reason', 'By'],
      ...movements.map((m) => [
        formatDateTimeInTz(m.created_at, timezone), KIND_LABEL[m.kind], m.order_number ?? '',
        (SIGN[m.kind] * m.amount).toFixed(2), m.tendered?.toFixed(2) ?? '', m.change_given?.toFixed(2) ?? '',
        m.reason ?? '', m.created_by_name ?? '',
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `till-${session.till_name.replace(/\W+/g, '-')}-${session.id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <PageHeader
        title={session.till_name}
        subtitle={open ? `Open since ${formatDateTimeInTz(session.opened_at, timezone)}` : `Closed ${session.closed_at ? formatDateTimeInTz(session.closed_at, timezone) : ''}`}
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4" /> CSV</Button>
            {open && canWork && (
              <>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_in')}>Cash in</Button>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_out')}>Cash out</Button>
                <Button size="sm" onClick={() => setDialog('close')}><Lock className="h-4 w-4" /> Close the till</Button>
              </>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm"><Link to="/nursery/tills" className="text-primary-700 hover:underline">All tills</Link></p>

        {t && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {([
              ['Float', t.opening_float],
              [`Cash sales (${t.sale_count})`, t.sales],
              ['Refunds', -t.refunds],
              ['Cash in', t.pay_ins],
              ['Cash out', -t.pay_outs],
              ['Should hold', t.expected_cash],
            ] as [string, number][]).map(([label, value]) => (
              <div key={label} className="bg-white border border-neutral-200 rounded-xl p-3 min-w-0">
                <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                <dd className="text-lg font-semibold text-neutral-900">{money(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {!open && (
          <section className={`border rounded-xl p-4 space-y-2 ${session.variance != null && Math.abs(session.variance) >= 0.005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className="font-semibold text-neutral-900 break-words">
              Counted {money(session.counted_cash ?? 0)} against {money(session.expected_cash ?? 0)}: {varianceText(session.variance)}
            </p>
            <p className="text-sm text-neutral-700 break-words">
              Closed by {session.closed_by_name ?? 'someone no longer here'}
              {session.closed_at ? ` on ${formatDateTimeInTz(session.closed_at, timezone)}` : ''}.
            </p>
            {session.close_notes && <p className="text-sm text-neutral-800 break-words">“{session.close_notes}”</p>}
            {session.closing_breakdown && <BreakdownLine breakdown={session.closing_breakdown} />}
          </section>
        )}

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Every movement</h2>
          <p className="text-xs text-neutral-500 break-words">
            Opened by {session.opened_by_name ?? 'someone no longer here'} with a float of {money(session.opening_float)}.
          </p>
          {session.opening_breakdown && <BreakdownLine breakdown={session.opening_breakdown} />}
          {movements.length === 0 ? (
            <p className="text-sm text-neutral-500">Nothing yet.</p>
          ) : (
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {movements.map((m) => <MovementRow key={m.id} m={m} timezone={timezone} />)}
            </ul>
          )}
        </section>
      </div>

      {(dialog === 'pay_in' || dialog === 'pay_out') && (
        <MovementDialog sessionId={session.id} kind={dialog} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'close' && t && (
        <CloseDialog session={session} expected={t.expected_cash} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
    </>
  );
}

function BreakdownLine({ breakdown }: { breakdown: CashBreakdown }) {
  const parts = Object.entries(breakdown)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([cents, n]) => `${n} × ${denominationLabel(Number(cents))}`);
  return <p className="text-xs text-neutral-600 break-words">Counted as {parts.join(', ')}.</p>;
}

function MovementRow({ m, timezone }: { m: TillMovement; timezone: string }) {
  const sign = SIGN[m.kind];
  return (
    <li className="px-4 py-3 flex items-start gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 break-words">
          {KIND_LABEL[m.kind]}
          {m.order_id && m.order_number && (
            <> · <Link to={`/nursery/orders/${m.order_id}`} className="text-primary-700 hover:underline">{m.order_number}</Link></>
          )}
        </p>
        <p className="text-xs text-neutral-500 break-words">
          {formatDateTimeInTz(m.created_at, timezone)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}
          {m.tendered != null ? ` · ${money(m.tendered)} handed over` : ''}
          {m.change_given ? `, ${money(m.change_given)} change` : ''}
        </p>
        {m.reason && m.kind !== 'refund' && <p className="text-xs text-neutral-700 break-words">{m.reason}</p>}
      </div>
      <p className={`text-sm font-semibold shrink-0 ${sign > 0 ? 'text-neutral-900' : 'text-red-700'}`}>
        {sign > 0 ? '+' : '−'}{money(m.amount)}
      </p>
    </li>
  );
}

function MovementDialog({ sessionId, kind, onClose, onDone }: {
  sessionId: number;
  kind: 'pay_in' | 'pay_out';
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const ok = value != null && value > 0 && reason.trim().length > 0;

  const save = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.addMovement(sessionId, { kind, amount: value, reason: reason.trim() });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, 'Not recorded'));
    } catch (e) {
      setError(errorMessage(e, 'Not recorded'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={kind === 'pay_in' ? 'Cash into the till' : 'Cash out of the till'}
      subtitle={kind === 'pay_in' ? 'Change brought in, or anything else put in the drawer.' : 'Banked, paid for something, or taken out for any other reason.'}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? 'Recording…' : 'Record'}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="move-amount" className="block text-sm font-medium text-neutral-700 mb-1">Amount</label>
        <input id="move-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      <div>
        <label htmlFor="move-reason" className="block text-sm font-medium text-neutral-700 mb-1">What it is for</label>
        <input id="move-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={input}
          placeholder={kind === 'pay_in' ? 'Change from the bank' : 'Banked at the end of the day'} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function CloseDialog({ session, expected, onClose, onDone }: {
  session: TillSession;
  expected: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const [count, setCount] = useState<CashBreakdown>({});
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const counted = cleanBreakdown(count);
  const value = counted ? breakdownTotal(counted) : parseAmount(typed);
  const variance = value != null ? round2(value - expected) : null;
  const out = variance != null && Math.abs(variance) >= 0.005;
  const ok = value != null && (!out || notes.trim().length > 0);

  const close = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.closeTill(session.id, { counted_cash: value, breakdown: counted, notes: notes.trim() || undefined });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, 'The till could not be closed'));
    } catch (e) {
      setError(errorMessage(e, 'The till could not be closed'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={`Close ${session.till_name}`}
      subtitle={`Count what is in the drawer. It should hold ${money(expected)}.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={close} disabled={!ok || busy}>{busy ? 'Closing…' : 'Close the till'}</Button>
        </>
      }
    >
      <CashCount idPrefix="close" value={count} onChange={setCount} denominations={session.denominations} />
      {!counted && (
        <div>
          <label htmlFor="close-typed" className="block text-sm font-medium text-neutral-700 mb-1">Or type the total counted</label>
          <input id="close-typed" type="text" inputMode="decimal" value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
        </div>
      )}
      {value != null && (
        <p className={`text-base font-semibold ${out ? 'text-red-700' : 'text-green-700'}`} role="status">
          Counted {money(value)}: {varianceText(variance)}
        </p>
      )}
      <div>
        <label htmlFor="close-notes" className="block text-sm font-medium text-neutral-700 mb-1">
          Note{out ? ' (needed: say why it is out)' : ''}
        </label>
        <textarea id="close-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

export default TillDetail;
